import { CombinedQuestionStats, ResponseLocationPoint } from "../view-survey.types";
import ResultsQuestion from "./results-question.component";
import { ReactElement, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import ResultsQuestionListHeader from "./results-question-list-header.component";
import { useViewSurveyChildren, ViewSurveyChildrenProps, type ViewSurveyChildrenOptions } from "../../survey-results/useViewSurveyChildren";
import { useGetSurveyAudienceQuery, useGetSurveyTypeQuery, useGetSurveyDemographicsQuery } from "src/redux/survey/survey.endpoints";
import { PulsePollingSurveyDataJSON, SurveyAudienceCriteria, SurveyStatus } from "src/redux/survey/survey.types";
import { SurveyResultsFilterBar } from "src/pages/survey-results/components/survey-results-filter-bar";
import { Attribute } from "src/components/criteria/criteria-settings";
import { toCityCase } from "src/helpers/string.helpers";

const SURVEY_TYPE_FILTER_KEYS = ['city', 'region', 'user_type'] as const;

export enum ResultsQuestionListTab {
  Summary, Responses, Demographics, PulsePolling
}

interface ResultsQuestionListProps extends ViewSurveyChildrenProps {
  readonly questions: CombinedQuestionStats[];
  readonly aiMaxCompletes?: number;
  readonly humanMaxCompletes?: number;
  readonly audienceId: string;
  readonly isShare: boolean;
  readonly pulsePollingData?: PulsePollingSurveyDataJSON;
  readonly statsOverride?: ViewSurveyChildrenOptions['statsOverride'];
  readonly summaryOverride?: ViewSurveyChildrenOptions['summaryOverride'];
  /** Merged survey group: sum of user totals across selected surveys (same for every row). */
  readonly groupAggregatedUserTotal?: number;
  /** When GET survey is skipped (group results), use this for question row chrome. */
  readonly surveyStatusOverride?: SurveyStatus;
  /** Survey group: do not prefix answer labels with A), B), … */
  readonly hideAnswerLetterPrefix?: boolean;
}

const ResultsQuestionList = ({
  questions,
  aiMaxCompletes,
  humanMaxCompletes,
  surveyId,
  audienceId,
  isShare,
  pulsePollingData,
  statsOverride,
  summaryOverride,
  groupAggregatedUserTotal,
  surveyStatusOverride,
  hideAnswerLetterPrefix,
}: ResultsQuestionListProps): ReactElement => {
  const [activeTab, setActiveTab] = useState<ResultsQuestionListTab>(ResultsQuestionListTab.Summary);
  const [resultsFilter, setResultsFilter] = useState<SurveyAudienceCriteria | undefined>(undefined);
  const [searchParams] = useSearchParams();
  const lastAnimatedIndex = useRef('0');
  const isMergedGroup = useMemo(
    () => groupAggregatedUserTotal !== undefined || questions.some((q) => Boolean(q.sourceExtSurveyId)),
    [questions, groupAggregatedUserTotal]
  );
  const { survey, stats } = useViewSurveyChildren(surveyId, isShare, {
    statsOverride,
    summaryOverride,
    skipStatsFetch: isMergedGroup,
    skipSummaryFetch: isMergedGroup,
    skipSurveyFetch: isMergedGroup,
  });

  const { criteria } = useGetSurveyAudienceQuery({ id: audienceId, isShare }, {
    skip: !audienceId,
    selectFromResult: ({ data }) => ({ criteria: data?.criteria }),
  });

  const { data: surveyType } = useGetSurveyTypeQuery(
    { ext_survey_id: surveyId, share: isShare },
    { skip: !surveyId || isMergedGroup }
  );

  const { data: demographicsData } = useGetSurveyDemographicsQuery(
    { ext_survey_id: surveyId, share: isShare },
    { skip: !surveyId || isMergedGroup }
  );

  /** Per-question map data: points filtered by question id, with answerTitle; showMapIcon when >= 70% have coords */
  const questionMapData = useMemo(() => {
    const map = new Map<string, { points: ResponseLocationPoint[]; showMapIcon: boolean }>();
    if (!demographicsData?.length) return map;

    const byQuestion = new Map<string, typeof demographicsData>();
    for (const record of demographicsData) {
      const qId = record.survey_question?.id;
      if (!qId) continue;
      if (!byQuestion.has(qId)) byQuestion.set(qId, []);
      byQuestion.get(qId)!.push(record);
    }

    byQuestion.forEach((records, questionId) => {
      const total = records.length;
      let withCoords = 0;
      const points = records
        .map((record) => {
          const demo = record.subject_demographics;
          if (!demo) return null;
          const latRaw = demo.latitude;
          const lngRaw = demo.longitude;
          const lat = typeof latRaw === 'string' ? parseFloat(latRaw) : typeof latRaw === 'number' ? latRaw : NaN;
          const lng = typeof lngRaw === 'string' ? parseFloat(lngRaw) : typeof lngRaw === 'number' ? lngRaw : NaN;
          if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
          withCoords += 1;
          const answerTitle = record.survey_question_answer?.title ?? record.answer_text ?? '';
          const point: ResponseLocationPoint = { lat, lng, answerTitle: answerTitle || undefined };
          if (typeof demo.city === 'string' && demo.city) point.city = demo.city;
          if (typeof demo.postal_code === 'string' && demo.postal_code) point.postal_code = demo.postal_code;
          if (typeof demo.region === 'string' && demo.region) point.region = demo.region;
          return point;
        })
        .filter((p): p is ResponseLocationPoint => p != null);
      const showMapIcon = total > 0 && withCoords / total >= 0.7;
      map.set(questionId, { points, showMapIcon });
    });
    return map;
  }, [demographicsData]);

  const surveyTypeFilterAttributes: Attribute[] = useMemo(() => {
    const attrs = surveyType?.attributes?.attributes;
    if (!attrs?.length) return [];
    return attrs
      .filter((a) => SURVEY_TYPE_FILTER_KEYS.includes(a.key as typeof SURVEY_TYPE_FILTER_KEYS[number]))
      .map((a) => ({
        label: toCityCase(a.display),
        value: a.key,
        options: a.values.map((v) => ({ label: toCityCase(v), value: v })),
      }));
  }, [surveyType]);

  const allowedFilterAttributes = useMemo(
    () => (surveyTypeFilterAttributes.length > 0 ? surveyTypeFilterAttributes.map((a) => a.value) : undefined),
    [surveyTypeFilterAttributes]
  );

  const activeFilter = resultsFilter ?? criteria;

  useEffect(() => {
    const questionIndex = searchParams.get('question');
    if (questionIndex && questionIndex !== 'summary' && questionIndex !== lastAnimatedIndex.current) {
      const element = document.getElementById(`results-question-${questionIndex}`);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
        lastAnimatedIndex.current = questionIndex;
      }
    }
  }, [searchParams]);


  const onTabChange = (tab: ResultsQuestionListTab) => {
    setActiveTab(tab);
  };

  const defaultAiUserTotal =
    isMergedGroup && groupAggregatedUserTotal !== undefined
      ? groupAggregatedUserTotal
      : stats?.total_users?.total ?? stats?.totalUsers ?? 0;

  return (
    <div className='flex flex-col'>
      {false && allowedFilterAttributes && surveyTypeFilterAttributes.length > 0 && (
        <SurveyResultsFilterBar
          initialFilter={criteria}
          onFilterChange={setResultsFilter}
          questions={questions}
          allowedAttributes={allowedFilterAttributes}
          surveyTypeAttributes={surveyTypeFilterAttributes}
        />
      )}
      <ResultsQuestionListHeader
        initialTab={activeTab}
        onTabChange={onTabChange}
        pulsePollingData={pulsePollingData}
      />
      <div className="flex flex-col gap-4">
        {questions.map((question, index) => {
          const mapKey = question.sourceExtSurveyId
            ? `${question.sourceExtSurveyId}:${question.id}`
            : question.id;
          const mapData = questionMapData.get(mapKey) ?? questionMapData.get(question.id);
          const rowSurveyId = question.sourceExtSurveyId ?? surveyId;
          const rowAiTotal = defaultAiUserTotal;
          return (
            <ResultsQuestion
              key={mapKey}
              listTab={activeTab}
              question={question}
              index={index}
              aiMaxCompletes={aiMaxCompletes}
              humanMaxCompletes={humanMaxCompletes}
              surveyId={rowSurveyId}
              initialFilter={activeFilter}
              surveyStatus={surveyStatusOverride ?? survey?.surveyStatus}
              hideAnswerLetterPrefix={hideAnswerLetterPrefix}
              pulsePollingData={pulsePollingData}
              aiUserTotal={rowAiTotal}
              humanUserTotal={0}
              surveyResponseLocations={mapData?.points ?? []}
              showMapIcon={mapData?.showMapIcon ?? false}
            />
          );
        })}
      </div>
    </div>
  );
};

export default ResultsQuestionList;
